/** Species composition: a table and three bar charts, as the prototype's summary blocks. */
import { useState } from 'react';
import { BarChart } from '@mantine/charts';
import { Box, Card, Group, SegmentedControl, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import { speciesColor } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { CHART_AXIS_PROPS, speciesBars } from './charts';
import { Section } from './Section';
import { useSpeciesName } from './species';
import type { SpeciesRow, TreeStats } from './stats';

type Mode = 'pct' | 'abs';

function ChartCard({ title, mode, onMode, absLabel, children }: { title: string; mode?: Mode; onMode?: (m: Mode) => void; absLabel?: string; children: React.ReactNode }) {
  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={700}>{title}</Text>
          {mode && onMode && (
            <SegmentedControl
              size="xs"
              className="report-controls"
              value={mode}
              onChange={(v) => onMode(v as Mode)}
              data={[
                { value: 'pct', label: '%' },
                { value: 'abs', label: absLabel ?? 'm³' },
              ]}
            />
          )}
        </Group>
        {children}
      </Stack>
    </Card>
  );
}

export function SpeciesSection({ stats }: { stats: TreeStats }) {
  const { t } = useTranslation('reports');
  const { number } = useFormat();
  const name = useSpeciesName();
  const [volumeMode, setVolumeMode] = useState<Mode>('pct');
  const [countMode, setCountMode] = useState<Mode>('pct');
  const { rows, total } = stats.species;
  const dash = '—';
  const fmt = (v: number | null, d = 0) => (v === null ? dash : number(v, d));

  const bars = (value: (r: SpeciesRow) => number | null, unit: string, digits: number) => {
    const { data, series } = speciesBars(rows, value, name);
    return (
      <BarChart
        h={Math.max(160, data.length * 28)}
        data={data}
        dataKey="species"
        type="stacked"
        orientation="vertical"
        series={series}
        withLegend={false}
        yAxisProps={{ width: 110 }}
        valueFormatter={(v) => `${number(v, digits)} ${unit}`}
        {...CHART_AXIS_PROPS}
      />
    );
  };

  const cell = (r: SpeciesRow, label: React.ReactNode) => (
    <Table.Tr key={r.genus || 'total'}>
      <Table.Td>{label}</Table.Td>
      <Table.Td ta="right">{number(r.countPct, 1)}</Table.Td>
      <Table.Td ta="right">{fmt(r.meanHeight, 1)}</Table.Td>
      <Table.Td ta="right">{fmt(r.meanDbh, 1)}</Table.Td>
      <Table.Td ta="right">{number(r.volume, 1)}</Table.Td>
      <Table.Td ta="right">{fmt(r.volumePerHa, 1)}</Table.Td>
    </Table.Tr>
  );

  return (
    <Section title={t('trees.sections.species')} className="report-species">
      <Stack gap="md">
        <Card>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('trees.species.table.species')}</Table.Th>
                  <Table.Th ta="right">{t('trees.species.table.share')}</Table.Th>
                  <Table.Th ta="right">{t('trees.species.table.meanHeight')}</Table.Th>
                  <Table.Th ta="right">{t('trees.species.table.meanDbh')}</Table.Th>
                  <Table.Th ta="right">{t('trees.species.table.volume')}</Table.Th>
                  <Table.Th ta="right">{t('trees.species.table.volumePerHa')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) =>
                  cell(
                    r,
                    <Group gap={8} wrap="nowrap">
                      <Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: speciesColor(r.genus), flex: '0 0 auto' }} />
                      <span>{name(r.genus)}</span>
                      <Text span fz={12} c={brand.textMuted} fs="italic">
                        {r.genus}
                      </Text>
                    </Group>,
                  ),
                )}
                {cell(total, <Text fw={700}>{t('trees.species.table.total')}</Text>)}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
          <ChartCard title={t('trees.species.charts.volume')} mode={volumeMode} onMode={setVolumeMode}>
            {volumeMode === 'pct' ? bars((r) => r.volumePct, '%', 1) : bars((r) => r.volume, 'm³', 1)}
          </ChartCard>
          <ChartCard title={t('trees.species.charts.count')} mode={countMode} onMode={setCountMode} absLabel={t('trees.species.trees')}>
            {countMode === 'pct' ? bars((r) => r.countPct, '%', 1) : bars((r) => r.count, t('trees.species.trees'), 0)}
          </ChartCard>
          <ChartCard title={t('trees.species.charts.volumePerHa')}>{bars((r) => r.volumePerHa, 'm³/ha', 1)}</ChartCard>
        </SimpleGrid>
      </Stack>
    </Section>
  );
}
